import { useState, useEffect, useCallback, useMemo } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Badge } from '@/components/common/Badge'
import { Loader2, Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { tokenResolutionApi } from '@/services/api/tokenResolution'
import { listBackgrounds, deleteBackground } from '@/services/api/qualification'
import type { BackgroundSuggestion } from '@/types/tokenResolution'

interface UnmatchedBackgroundDialogProps {
  open: boolean
  token: string
  tokens?: string[]
  sessionId?: string
  onResolve: (resolutionType: 'alias' | 'new_background', data: Record<string, unknown>) => void
  onClose: () => void
}

export function UnmatchedBackgroundDialog({
  open,
  token,
  tokens,
  sessionId,
  onResolve,
  onClose,
}: UnmatchedBackgroundDialogProps) {
  const { t } = useTranslation()
  const allTokens = useMemo(() => {
    const list = tokens && tokens.length > 0 ? tokens : [token]
    return [...new Set(list.filter(Boolean))]
  }, [tokens, token])
  const [activeTab, setActiveTab] = useState<'alias' | 'new'>('alias')
  const [backgroundSuggestions, setBackgroundSuggestions] = useState<BackgroundSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedBackgroundIds, setSelectedBackgroundIds] = useState<Set<string>>(new Set())

  const [backgroundName, setBackgroundName] = useState(token)
  const [backgroundCode, setBackgroundCode] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [deletingBackgroundId, setDeletingBackgroundId] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setBackgroundName(token)
      setSelectedBackgroundIds(new Set())
      setBackgroundCode('')
      setActiveTab('alias')

      loadSuggestions()
    }
  }, [open, token])

  const loadSuggestions = useCallback(async () => {
    setLoading(true)

    try {
      // Fetch ALL backgrounds instead of fuzzy suggestions
      const allBackgrounds = await listBackgrounds()
      const suggestions: BackgroundSuggestion[] = allBackgrounds.map(b => ({
        background_id: b.id,
        name: b.name,
        code: b.code,
        similarity_score: 0,
        is_preselected: false,
        is_alias: false,
      }))
      setBackgroundSuggestions(suggestions)
    } catch (err) {
      console.error('Failed to load backgrounds:', err)
      // Fallback to fuzzy suggestions if all backgrounds endpoint fails
      try {
        const backgrounds = await tokenResolutionApi.getBackgroundSuggestions(token)
        setBackgroundSuggestions(backgrounds)
      } catch {
        // Silent fail
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  const toggleBackground = useCallback((backgroundId: string) => {
    setSelectedBackgroundIds(prev => {
      const next = new Set(prev)
      if (next.has(backgroundId)) {
        next.delete(backgroundId)
      } else {
        next.add(backgroundId)
      }
      return next
    })
  }, [])

  const handleDeleteBackground = useCallback(async (backgroundId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setDeletingBackgroundId(backgroundId)
    try {
      await deleteBackground(backgroundId)
      setSelectedBackgroundIds(prev => {
        const next = new Set(prev)
        next.delete(backgroundId)
        return next
      })
      loadSuggestions()
    } catch (err) {
      console.error('Failed to delete background:', err)
    } finally {
      setDeletingBackgroundId(null)
    }
  }, [loadSuggestions])

  const handleCreateAlias = useCallback(async () => {
    if (selectedBackgroundIds.size === 0) return

    setSubmitting(true)
    try {
      const selectedIds = Array.from(selectedBackgroundIds)

      if (selectedIds.length === 1) {
        for (const aliasName of allTokens) {
          await tokenResolutionApi.createBackgroundAlias({
            alias_name: aliasName,
            canonical_background_id: selectedIds[0]!,
          })
        }

        onResolve('alias', {
          alias_name: token,
          canonical_background_id: selectedIds[0],
          tokens: allTokens,
        })
      } else {
        const aliasResults = []
        for (const aliasName of allTokens) {
          for (const backgroundId of selectedIds) {
            await tokenResolutionApi.createBackgroundAlias({
              alias_name: aliasName,
              canonical_background_id: backgroundId,
            })
            aliasResults.push({ alias_name: aliasName, canonical_background_id: backgroundId })
          }
        }

        onResolve('alias', {
          alias_name: token,
          canonical_background_ids: selectedIds,
          alias_results: aliasResults,
          tokens: allTokens,
        })
      }
      onClose()
    } catch (err) {
      console.error('Failed to create alias:', err)
    } finally {
      setSubmitting(false)
    }
  }, [token, allTokens, selectedBackgroundIds, onResolve, onClose])

  const handleCreateBackground = useCallback(async () => {
    setSubmitting(true)
    try {
      const created = await tokenResolutionApi.createBackground({
        name: backgroundName,
        code: backgroundCode || undefined,
      }, sessionId)

      if (created?.id) {
        const backgroundNameLower = backgroundName.toLowerCase()
        for (const aliasName of allTokens) {
          if (aliasName.toLowerCase() === backgroundNameLower) continue
          await tokenResolutionApi.createBackgroundAlias({
            alias_name: aliasName,
            canonical_background_id: created.id,
          })
        }
      }

      onResolve('new_background', {
        background_name: backgroundName,
        background_code: backgroundCode,
        background_id: created?.id,
        tokens: allTokens,
      })
      onClose()
    } catch (err) {
      console.error('Failed to create background:', err)
    } finally {
      setSubmitting(false)
    }
  }, [backgroundName, backgroundCode, sessionId, allTokens, onResolve, onClose])

  if (!open) return null

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t('unmatchedBackgroundDialog_title', 'Unbekannter Hintergrund: {{token}}', { token: allTokens.length > 1 ? `${allTokens.length} Token` : token })}
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'alias'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('alias')}
          >
            {t('unmatchedBackgroundDialog_tabAlias', 'Alias zuordnen')}
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'new'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('new')}
          >
            {t('unmatchedBackgroundDialog_tabNew', 'Neuen Hintergrund erstellen')}
          </button>
        </div>

        {activeTab === 'alias' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('unmatchedBackgroundDialog_aliasDescription', 'Ordnen Sie "{{token}}" einem oder mehreren bestehenden Hintergründen als Alias zu.', { token })}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-500">{t('unmatchedBackgroundDialog_loading', 'Vorschläge laden...')}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('unmatchedBackgroundDialog_selectBackground', 'Hintergrund auswählen (Mehrfachauswahl möglich)')}
                </label>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {backgroundSuggestions.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {t('unmatchedBackgroundDialog_noSuggestions', 'Keine Vorschläge gefunden')}
                    </div>
                  ) : (
                    backgroundSuggestions.map(suggestion => (
                      <div
                        key={suggestion.background_id}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 ${
                          selectedBackgroundIds.has(suggestion.background_id) ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                        }`}
                      >
                        <button
                          className="flex-1 flex items-center gap-2"
                          onClick={() => toggleBackground(suggestion.background_id)}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            selectedBackgroundIds.has(suggestion.background_id)
                              ? 'bg-blue-500 border-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {selectedBackgroundIds.has(suggestion.background_id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          {suggestion.is_preselected && <Check className="h-4 w-4 text-blue-500" />}
                          <span className="font-medium">{suggestion.name}</span>
                          {suggestion.code && (
                            <Badge className="text-xs bg-gray-100 text-gray-600">{suggestion.code}</Badge>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">{suggestion.similarity_score}%</span>
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-0.5 flex-shrink-0"
                          onClick={(e) => handleDeleteBackground(suggestion.background_id, e)}
                          disabled={deletingBackgroundId === suggestion.background_id}
                          title="Hintergrund löschen"
                        >
                          {deletingBackgroundId === suggestion.background_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <X className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={onClose}>
                {t('unmatchedBackgroundDialog_cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={handleCreateAlias}
                disabled={selectedBackgroundIds.size === 0 || submitting}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('unmatchedBackgroundDialog_createAlias', 'Alias erstellen')}
              </Button>
            </div>

            <div className="pt-2 text-center">
              <button
                className="text-sm text-blue-500 hover:underline"
                onClick={() => setActiveTab('new')}
              >
                {t('unmatchedBackgroundDialog_noMatch', 'Kein passender Hintergrund? Neuen erstellen')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'new' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t('unmatchedBackgroundDialog_backgroundName', 'Hintergrund-Name')}
              </label>
              <Input
                value={backgroundName}
                onChange={e => setBackgroundName(e.target.value)}
                placeholder={t('unmatchedBackgroundDialog_backgroundNamePlaceholder', 'Hintergrund-Name eingeben')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t('unmatchedBackgroundDialog_backgroundCode', 'Hintergrund-Code (optional)')}
              </label>
              <Input
                value={backgroundCode}
                onChange={e => setBackgroundCode(e.target.value)}
                placeholder={t('unmatchedBackgroundDialog_backgroundCodePlaceholder', '')}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={onClose}>
                {t('unmatchedBackgroundDialog_cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={handleCreateBackground}
                disabled={!backgroundName || submitting}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('unmatchedBackgroundDialog_create', 'Erstellen')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}